import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import DOMPurify from "dompurify";
import {
  ArrowLeft, BookOpen, Users, Layers, GraduationCap, Calendar, CreditCard,
  Pencil, Plus, Trash2, Clock, Shield, ChevronRight
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusLabels = { starting_soon: "Starting Soon", in_progress: "In Progress", completed: "Completed" };

export default function AdminCourseDashboard() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  // Group dialogs
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: "", schedule: "", status: "starting_soon" });

  // Module create dialog
  const [moduleDialog, setModuleDialog] = useState(false);
  const [moduleForm, setModuleForm] = useState({ title: "", description: "" });

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/courses/${courseId}/dashboard`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/admin/courses");
    } finally {
      setLoading(false);
    }
  }, [courseId, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-[#5C3A2E]/20 border-t-[#5C3A2E] rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!data) return null;

  const { course, groups, modules, students, payments, sessions, instructors, stats } = data;

  // Group handlers
  const openCreateGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: "", schedule: "", status: "starting_soon", current_module_id: "none" });
    setGroupDialog(true);
  };
  const openEditGroup = (g) => {
    setEditingGroup(g);
    setGroupForm({ name: g.name, schedule: g.schedule || "", status: g.status || "starting_soon", current_module_id: g.current_module_id || "none" });
    setGroupDialog(true);
  };
  const saveGroup = async () => {
    try {
      const payload = { ...groupForm };
      if (payload.current_module_id === "none") payload.current_module_id = null;
      if (editingGroup) {
        await api.put(`/groups/${editingGroup.id}`, payload);
        toast.success("Group updated");
      } else {
        await api.post("/groups", { ...groupForm, course_id: courseId });
        toast.success("Group created");
      }
      setGroupDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try { await api.delete(`/groups/${g.id}`); toast.success("Group deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Module handlers
  const openCreateModule = () => {
    setModuleForm({ title: "", description: "" });
    setModuleDialog(true);
  };
  const saveModule = async () => {
    try {
      await api.post("/modules", { ...moduleForm, course_id: courseId, order: modules.length });
      toast.success("Module created");
      setModuleDialog(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteModule = async (m) => {
    if (!window.confirm(`Delete module "${m.title}"?`)) return;
    try { await api.delete(`/modules/${m.id}`); toast.success("Module deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const getGroupName = (id) => groups.find((g) => g.id === id)?.name || "Unassigned";
  const getModuleName = (id) => modules.find((m) => m.id === id)?.title || "None";
  const studentsInGroup = (gid) => students.filter((s) => s.group_id === gid);

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "groups", label: `Groups (${stats.total_groups})` },
    { key: "modules", label: `Modules (${stats.total_modules})` },
    { key: "students", label: `Students (${stats.total_students})` },
    { key: "payments", label: "Payments" },
  ];

  return (
    <AdminLayout>
      <div data-testid="course-dashboard" className="space-y-6 animate-fade-in-up">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Button data-testid="back-to-courses" variant="ghost" size="sm" onClick={() => navigate("/admin/courses")} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full mt-1 h-8 w-8 p-0 flex-shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-5 h-5 text-[#C6A969]" />
              <h1 className="font-heading text-2xl sm:text-3xl text-[#5C3A2E] font-semibold tracking-tight">{course.title}</h1>
            </div>
            {course.subtitle && <p className="text-[#2B2B2B]/50 text-sm italic">{course.subtitle}</p>}
            {instructors.length > 0 && (
              <p className="text-xs text-[#C6A969] mt-1 flex items-center gap-1">
                <Shield className="w-3 h-3" /> {instructors.map((i) => i.name).join(", ")}
              </p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeTab === t.key
                  ? "bg-[#5C3A2E] text-white shadow-[0_2px_8px_rgb(92,58,46,0.2)]"
                  : "bg-white text-[#2B2B2B]/60 hover:bg-[#EADBC8]/40 hover:text-[#5C3A2E] border border-[#EADBC8]/30"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "overview" && <OverviewTab stats={stats} course={course} groups={groups} modules={modules} students={students} payments={payments} setActiveTab={setActiveTab} />}
        {activeTab === "groups" && <GroupsTab groups={groups} modules={modules} students={students} studentsInGroup={studentsInGroup} getModuleName={getModuleName} openCreateGroup={openCreateGroup} openEditGroup={openEditGroup} deleteGroup={deleteGroup} />}
        {activeTab === "modules" && <ModulesTab modules={modules} courseId={courseId} openCreateModule={openCreateModule} deleteModule={deleteModule} />}
        {activeTab === "students" && <StudentsTab students={students} groups={groups} modules={modules} getGroupName={getGroupName} />}
        {activeTab === "payments" && <PaymentsTab payments={payments} students={students} />}

        {/* Group Dialog */}
        <Dialog open={groupDialog} onOpenChange={setGroupDialog}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">{editingGroup ? "Edit Group" : "New Group"}</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">{editingGroup ? "Update group details" : "Create a new time slot group for this course"}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Name</label>
                <Input data-testid="group-name-input" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="e.g., Wednesdays 5pm-7pm" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Schedule</label>
                <Input data-testid="group-schedule-input" value={groupForm.schedule} onChange={(e) => setGroupForm({ ...groupForm, schedule: e.target.value })} placeholder="e.g., Wed 5:00 PM - 7:00 PM" className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Status</label>
                <Select value={groupForm.status} onValueChange={(v) => setGroupForm({ ...groupForm, status: v })}>
                  <SelectTrigger data-testid="group-status-select" className="rounded-xl border-[#EADBC8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="starting_soon">Starting Soon</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Current Module</label>
                <Select value={groupForm.current_module_id} onValueChange={(v) => setGroupForm({ ...groupForm, current_module_id: v })}>
                  <SelectTrigger data-testid="group-module-select" className="rounded-xl border-[#EADBC8]"><SelectValue placeholder="Select module" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {modules.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-[#2B2B2B]/40 mt-1">This determines which module students in this group are currently working on</p>
              </div>
              <Button data-testid="save-group-button" onClick={saveGroup} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                {editingGroup ? "Save Changes" : "Create Group"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Module Dialog */}
        <Dialog open={moduleDialog} onOpenChange={setModuleDialog}>
          <DialogContent className="bg-white rounded-xl max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#5C3A2E]">New Module</DialogTitle>
              <DialogDescription className="text-[#2B2B2B]/50 text-sm">Add a learning module to this course</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Title</label>
                <Input data-testid="module-title-input" value={moduleForm.title} onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2B2B] mb-1 block">Description</label>
                <Input data-testid="module-desc-input" value={moduleForm.description} onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })} className="rounded-xl border-[#EADBC8]" />
              </div>
              <Button data-testid="save-module-button" onClick={saveModule} className="w-full bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 h-11">
                Create Module
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

/* ========== TAB COMPONENTS ========== */

function OverviewTab({ stats, course, groups, modules, students, payments, setActiveTab }) {
  const statCards = [
    { label: "Groups", value: stats.total_groups, icon: Users, color: "#5C3A2E", tab: "groups" },
    { label: "Modules", value: stats.total_modules, icon: Layers, color: "#C6A969", tab: "modules" },
    { label: "Students", value: stats.total_students, icon: GraduationCap, color: "#5C3A2E", tab: "students" },
    { label: "Sessions", value: stats.total_sessions, icon: Calendar, color: "#C6A969" },
    { label: "Paid", value: `${stats.total_paid.toFixed(0)}`, icon: CreditCard, color: "#5C3A2E", tab: "payments" },
    { label: "Pending", value: `${stats.total_pending.toFixed(0)}`, icon: CreditCard, color: "#C6A969", tab: "payments" },
  ];

  return (
    <div className="space-y-6">
      {course.description && (
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5">
          <div className="prose prose-sm max-w-none text-[#2B2B2B]/70" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course.description) }} />
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <button
              key={card.label + i}
              data-testid={`course-stat-${card.label.toLowerCase()}`}
              onClick={() => card.tab && setActiveTab(card.tab)}
              className={`bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 text-left studio-card animate-fade-in-up ${card.tab ? "cursor-pointer" : "cursor-default"}`}
              style={{ animationDelay: `${i * 0.05}s` }}
            >
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${card.color}15` }}>
                <Icon className="w-4.5 h-4.5" style={{ color: card.color }} />
              </div>
              <p className="text-2xl font-semibold text-[#2B2B2B] font-body">{card.value}</p>
              <p className="text-xs text-[#2B2B2B]/50 mt-0.5">{card.label}</p>
            </button>
          );
        })}
      </div>

      {/* Quick preview: recent students */}
      {students.length > 0 && (
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base text-[#5C3A2E] font-semibold">Recent Students</h3>
            <button onClick={() => setActiveTab("students")} className="text-xs text-[#C6A969] hover:text-[#5C3A2E] transition-colors font-medium flex items-center gap-0.5">
              View all <ChevronRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            {students.slice(0, 5).map((s) => (
              <div key={s.id} className="flex items-center gap-3 py-1.5">
                <div className="w-7 h-7 rounded-full bg-[#EADBC8] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {s.avatar_url ? (
                    <img src={`${API_BASE}/api/files/${s.avatar_url}`} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[10px] font-semibold text-[#5C3A2E]">{s.name?.[0]?.toUpperCase()}</span>
                  )}
                </div>
                <span className="text-sm text-[#2B2B2B] truncate flex-1">{s.name}</span>
                <span className="text-[10px] text-[#2B2B2B]/40 truncate">{s.email}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GroupsTab({ groups, modules, students, studentsInGroup, getModuleName, openCreateGroup, openEditGroup, deleteGroup }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="create-group-button" onClick={openCreateGroup} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
          <Plus className="w-4 h-4 mr-1.5" /> New Group
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {groups.map((g) => {
          const count = studentsInGroup(g.id).length;
          return (
            <div key={g.id} data-testid={`group-card-${g.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 studio-card">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{g.name}</h3>
                <div className="flex gap-1">
                  <Button data-testid={`edit-group-${g.id}`} variant="ghost" size="sm" onClick={() => openEditGroup(g)} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-7 w-7 p-0">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button data-testid={`delete-group-${g.id}`} variant="ghost" size="sm" onClick={() => deleteGroup(g)} className="text-red-400 hover:bg-red-50 rounded-full h-7 w-7 p-0">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1.5 text-sm text-[#2B2B2B]/60">
                <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /><span>{g.schedule || "No schedule"}</span></div>
                <div className="flex items-center gap-2"><Layers className="w-3.5 h-3.5" /><span>{g.current_module_id ? getModuleName(g.current_module_id) : "No module"}</span></div>
                <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /><span>{count} student{count !== 1 ? "s" : ""}</span></div>
              </div>
              <div className="mt-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider status-${g.status || "starting_soon"}`}>
                  {statusLabels[g.status] || "Starting Soon"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {groups.length === 0 && (
        <div className="text-center py-10 text-[#2B2B2B]/40 text-sm">No groups yet. Create one to get started.</div>
      )}
    </div>
  );
}

function ModulesTab({ modules, courseId, openCreateModule, deleteModule }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button data-testid="create-module-button" onClick={openCreateModule} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90">
          <Plus className="w-4 h-4 mr-1.5" /> New Module
        </Button>
      </div>
      <div className="grid gap-3">
        {modules.map((m, i) => (
          <div key={m.id} data-testid={`module-card-${m.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 studio-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-[#C6A969] font-medium">Module {i + 1}</span>
                </div>
                <h3 className="font-heading text-base text-[#5C3A2E] font-semibold">{m.title}</h3>
                {m.description && <div className="text-[#2B2B2B]/50 text-sm mt-1 line-clamp-2 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.description) }} />}
                <div className="flex items-center gap-3 mt-2 text-xs text-[#2B2B2B]/40">
                  {m.materials?.length > 0 && <span>{m.materials.length} materials</span>}
                  {m.images?.length > 0 && <span>{m.images.length} images</span>}
                  {m.syllabus_path && <span>Syllabus</span>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Link to={`/admin/modules/${m.id}`}>
                  <Button data-testid={`edit-module-${m.id}`} variant="ghost" size="sm" className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-7 w-7 p-0">
                    <Pencil className="w-3 h-3" />
                  </Button>
                </Link>
                <Button data-testid={`delete-module-${m.id}`} variant="ghost" size="sm" onClick={() => deleteModule(m)} className="text-red-400 hover:bg-red-50 rounded-full h-7 w-7 p-0">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {modules.length === 0 && (
        <div className="text-center py-10">
          <Layers className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
          <p className="text-[#2B2B2B]/40 text-sm">No modules yet. Add your first module.</p>
        </div>
      )}
    </div>
  );
}

function StudentsTab({ students, groups, modules, getGroupName }) {
  const getCurrentModule = (s) => {
    if (s.override_module_id) {
      const m = modules.find((mod) => mod.id === s.override_module_id);
      return m?.title || "Override";
    }
    if (s.group_id) {
      const g = groups.find((gr) => gr.id === s.group_id);
      if (g?.current_module_id) {
        const m = modules.find((mod) => mod.id === g.current_module_id);
        return m?.title || "None";
      }
    }
    return "None";
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-[#2B2B2B]/50">{students.length} student{students.length !== 1 ? "s" : ""} enrolled in this course</p>
      {students.map((s) => (
        <div key={s.id} data-testid={`student-card-${s.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-4 studio-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#EADBC8] flex items-center justify-center overflow-hidden flex-shrink-0">
              {s.avatar_url ? (
                <img src={`${API_BASE}/api/files/${s.avatar_url}`} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs font-semibold text-[#5C3A2E]">{s.name?.[0]?.toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-medium text-[#2B2B2B] truncate">{s.name}</h4>
              <p className="text-[10px] text-[#2B2B2B]/40 truncate">{s.email}</p>
            </div>
            <div className="hidden sm:flex items-center gap-4 text-xs text-[#2B2B2B]/50">
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{getGroupName(s.group_id)}</span>
              <span className="flex items-center gap-1 max-w-[140px] truncate"><Layers className="w-3 h-3" />{getCurrentModule(s)}</span>
            </div>
          </div>
        </div>
      ))}
      {students.length === 0 && (
        <div className="text-center py-10">
          <GraduationCap className="w-10 h-10 text-[#EADBC8] mx-auto mb-3" />
          <p className="text-[#2B2B2B]/40 text-sm">No students enrolled in this course yet.</p>
        </div>
      )}
    </div>
  );
}

function PaymentsTab({ payments, students }) {
  const getStudentName = useCallback((id) => students.find((s) => s.id === id)?.name || "Unknown", [students]);
  const paid = useMemo(() => payments.filter((p) => p.status === "paid"), [payments]);
  const pending = useMemo(() => payments.filter((p) => p.status === "pending" || p.status === "overdue"), [payments]);
  const paidTotal = useMemo(() => paid.reduce((s, p) => s + (p.amount || 0), 0).toFixed(0), [paid]);
  const pendingTotal = useMemo(() => pending.reduce((s, p) => s + (p.amount || 0), 0).toFixed(0), [pending]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5">
          <p className="text-xs text-[#2B2B2B]/50 mb-1">Total Paid</p>
          <p className="text-2xl font-semibold text-[#5C3A2E] font-body">{paidTotal}</p>
          <p className="text-[10px] text-[#2B2B2B]/40 mt-0.5">{paid.length} payment{paid.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5">
          <p className="text-xs text-[#2B2B2B]/50 mb-1">Pending / Overdue</p>
          <p className="text-2xl font-semibold text-[#C6A969] font-body">{pendingTotal}</p>
          <p className="text-[10px] text-[#2B2B2B]/40 mt-0.5">{pending.length} payment{pending.length !== 1 ? "s" : ""}</p>
        </div>
      </div>
      <div className="space-y-2">
        {payments.slice(0, 20).map((p) => (
          <div key={p.id} className="bg-white rounded-xl border border-[#EADBC8]/30 p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#2B2B2B]">{getStudentName(p.student_id)}</p>
              <p className="text-[10px] text-[#2B2B2B]/40">{p.due_date || "No due date"}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-[#2B2B2B]">{p.amount}</p>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                p.status === "paid" ? "bg-[#5C3A2E]/10 text-[#5C3A2E]" :
                p.status === "overdue" ? "bg-red-100 text-red-700" :
                "bg-[#C6A969]/20 text-[#5C3A2E]"
              }`}>
                {p.status}
              </span>
            </div>
          </div>
        ))}
      </div>
      {payments.length === 0 && (
        <div className="text-center py-10 text-[#2B2B2B]/40 text-sm">No payments recorded for this course.</div>
      )}
    </div>
  );
}
